import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Badge from '../components/atoms/Badge';
import Icon from '../components/atoms/Icon';
import { analyzeMatch, enhanceResume, getCourses } from '../services/api';

const MatchResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [enhancement, setEnhancement] = useState(null);
  const [courses, setCourses] = useState([]);
  const [showEnhancement, setShowEnhancement] = useState(false);
  const [checkingJobType, setCheckingJobType] = useState(false);

  // Get application_id from location state
  const applicationId = location.state?.applicationId;
  
  console.log('🔍 MatchResultPage - applicationId from location.state:', applicationId);
  console.log('🔍 MatchResultPage - full location.state:', location.state);

  useEffect(() => {
    if (!applicationId) {
      navigate('/jobs');
      return;
    }
    loadAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const loadAnalysis = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await analyzeMatch(applicationId);
      setAnalysis(data);
      
      // Load all courses to match with recommendations
      const allCourses = await getCourses();
      setCourses(allCourses);
    } catch (err) {
      setError(err.message || 'Failed to load match analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleEnhanceResume = async () => {
    setEnhancing(true);
    try {
      const data = await enhanceResume(applicationId);
      setEnhancement(data.suggestions);
      setShowEnhancement(true);
    } catch (err) {
      alert(err.message || 'Failed to generate enhancement suggestions');
    } finally {
      setEnhancing(false);
    }
  };

  const handleStartAssessment = async () => {
    setCheckingJobType(true);
    try {
      console.log('🔍 Starting assessment check for applicationId:', applicationId);
      
      // Get job details to check if live interview is enabled
      const applicationUrl = `${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/applications/${applicationId}`;
      console.log('🔍 Fetching application from:', applicationUrl);
      
      const response = await fetch(applicationUrl);
      console.log('🔍 Application response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Application fetch failed:', response.status, errorText);
        throw new Error(`Failed to get application details: ${response.status} - ${errorText}`);
      }
      
      const application = await response.json();
      console.log('🔍 Application details:', application);
      
      // Get job details
      const jobUrl = `${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/jobs/${application.job_id}`;
      console.log('🔍 Fetching job from:', jobUrl);
      
      const jobResponse = await fetch(jobUrl);
      console.log('🔍 Job response status:', jobResponse.status);
      
      if (!jobResponse.ok) {
        const errorText = await jobResponse.text();
        console.error('❌ Job fetch failed:', jobResponse.status, errorText);
        throw new Error(`Failed to get job details: ${jobResponse.status} - ${errorText}`);
      }
      
      const job = await jobResponse.json();
      
      console.log('🔍 Job details:', job);
      console.log('🔍 Live interview enabled:', job.live_interview_enabled);
      console.log('🔍 Live interview config:', job.live_interview_config);
      
      const candidateName = localStorage.getItem('dutlaunch_candidate_name') || 'Candidate';
      
      // Check if live interview is enabled for this job
      if (job.live_interview_enabled && job.live_interview_config) {
        console.log('✅ Routing to live interview welcome page');
        // Navigate to live interview welcome page with job configuration
        navigate('/live-interview/welcome', {
          state: {
            applicationId: applicationId,
            jobTitle: job.title,
            candidateName: candidateName,
            interviewType: 'interviewer', // This is a real interview, not training
            language: job.live_interview_config.language || 'en-US',
            duration: job.live_interview_config.duration_minutes || 5,
            focusAreas: job.live_interview_config.focus_areas || ['technical', 'behavioral']
          }
        });
      } else {
        console.log('❌ Live interview not enabled, routing to regular assessment');
        console.log('   - live_interview_enabled:', job.live_interview_enabled);
        console.log('   - live_interview_config:', job.live_interview_config);
        // Navigate to regular assessment flow
        navigate('/assessment/welcome', {
          state: {
            applicationId: applicationId,
            jobTitle: analysis?.job_title || 'Position',
            candidateName: candidateName
          }
        });
      }
    } catch (error) {
      console.error('Error checking job interview type:', error);
      // Fallback to regular assessment if there's an error
      const candidateName = localStorage.getItem('dutlaunch_candidate_name') || 'Candidate';
      navigate('/assessment/welcome', {
        state: {
          applicationId: applicationId,
          jobTitle: analysis?.job_title || 'Position',
          candidateName: candidateName
        }
      });
    } finally {
      setCheckingJobType(false);
    }
  };

  const handleViewCourse = (courseTitle) => {
    // Find course by title and navigate to courses page
    navigate('/courses', { state: { searchQuery: courseTitle } });
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="relative flex h-screen w-full flex-col overflow-x-hidden">
        <LandingHeader />
        <main className="layout-container flex h-full grow flex-col items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-light-secondary dark:text-text-dark-secondary">
              Analyzing your resume match...
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="relative flex h-screen w-full flex-col overflow-x-hidden">
        <LandingHeader />
        <main className="layout-container flex h-full grow flex-col items-center justify-center">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700 max-w-md">
            {error || 'Failed to load analysis'}
          </div>
          <Button className="mt-4" onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden">
      <LandingHeader />
      
      <main className="layout-container flex h-full grow flex-col">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="layout-content-container flex flex-col w-full flex-1">
            {/* Breadcrumb */}
            <div className="flex flex-wrap gap-2 pb-6">
              <button
                type="button"
                onClick={() => navigate('/jobs')}
                className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal hover:text-primary"
              >
                Jobs
              </button>
              <span className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal">/</span>
              <span className="text-text-light-primary dark:text-text-dark-primary text-sm font-medium leading-normal">
                Match Result
              </span>
            </div>

            {/* Page Title */}
            <div className="flex flex-col gap-2 pb-8">
              <h1 className="text-text-light-primary dark:text-text-dark-primary text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">
                Match Result
              </h1>
            </div>

            {/* Match Score Card */}
            <div className={`rounded-xl border p-6 mb-6 ${getScoreBgColor(analysis.match_score)}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-text-light-primary dark:text-text-dark-primary">
                  Match Score
                </h2>
                <span className={`text-4xl font-black ${getScoreColor(analysis.match_score)}`}>
                  {analysis.match_score.toFixed(1)}%
                </span>
              </div>
              
              {/* Explanation */}
              <div className="mb-4">
                <h3 className="text-base font-bold text-text-light-primary dark:text-text-dark-primary mb-2">
                  Explanation:
                </h3>
                <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed whitespace-pre-line">
                  {analysis.explanation}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6">
                {analysis.can_start_assessment ? (
                  <Button 
                    size="lg" 
                    onClick={handleStartAssessment}
                    disabled={checkingJobType}
                  >
                    <Icon name="play_arrow" size={20} className="mr-2" />
                    {checkingJobType ? 'Checking...' : 'Start Interview'}
                  </Button>
                ) : (
                  <Button size="lg" variant="secondary" onClick={() => handleViewCourse(analysis.recommended_courses[0]?.course_title)}>
                    <Icon name="school" size={20} className="mr-2" />
                    View Related Courses
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/enhance-resume')}
                >
                  <Icon name="auto_fix_high" size={20} className="mr-2" />
                  Enhance Resume
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/jobs')}>
                  Back to Jobs
                </Button>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="flex flex-col gap-6">
                {/* Match Reasons */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                    <Icon name="check_circle" size={24} className="mr-2 text-green-600" />
                    Why You Match
                  </h3>
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed">
                    {analysis.match_reason}
                  </p>
                </div>

                {/* Strengths */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                    Your Strengths
                  </h3>
                  <ul className="space-y-2">
                    {analysis.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="star" size={18} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                          {strength}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Resume Enhancement Tips */}
                {showEnhancement && enhancement && (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="auto_fix_high" size={24} className="mr-2 text-primary" />
                      Resume Enhancement Suggestions
                    </h3>
                    <div className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed whitespace-pre-line">
                      {enhancement}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-6">
                {/* Not Match Reasons */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                    <Icon name="info" size={24} className="mr-2 text-orange-600" />
                    Areas for Improvement
                  </h3>
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary leading-relaxed">
                    {analysis.not_match_reason}
                  </p>
                </div>

                {/* Skill Gaps */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                    Skill Gaps
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {analysis.gaps.map((gap, index) => (
                      <Badge key={index} variant="secondary">
                        {gap}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Recommended Courses */}
                {analysis.recommended_courses && analysis.recommended_courses.length > 0 && (
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4 flex items-center">
                      <Icon name="school" size={24} className="mr-2 text-primary" />
                      Recommended Courses
                    </h3>
                    <div className="space-y-4">
                      {analysis.recommended_courses.map((rec, index) => {
                        // Find full course details
                        const course = courses.find(c => c.title === rec.course_title);
                        
                        return (
                          <div key={index} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-sm text-text-light-primary dark:text-text-dark-primary">
                                {rec.course_title}
                              </h4>
                              {course && (
                                <Badge variant="default">{course.level}</Badge>
                              )}
                            </div>
                            <p className="text-xs text-text-light-secondary dark:text-text-dark-secondary mb-3">
                              {rec.reason}
                            </p>
                            {course && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 text-xs text-text-light-secondary dark:text-text-dark-secondary">
                                  <span>{course.provider}</span>
                                  <span>•</span>
                                  <span>{course.duration_weeks} weeks</span>
                                  <span>•</span>
                                  <span className="font-semibold text-primary">${course.price}</span>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleViewCourse(rec.course_title)}
                                >
                                  View
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quick Tips */}
                <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                  <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                    Quick Tips
                  </h3>
                  <ul className="space-y-2">
                    {analysis.resume_enhancement_tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="lightbulb" size={18} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                          {tip}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MatchResultPage;
